import { styled } from "@mui/material/styles";
import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { reducedMotion } from "./tokens";
import { usePresence } from "./usePresence";
const SHEET_MS = 320;
const TOP_GAP = 40;
const Scrim = styled("div", {
    shouldForwardProp: (prop)=>prop !== "isVisible"
})(({ isVisible })=>({
        position: "fixed",
        inset: 0,
        zIndex: 1200,
        background: "rgb(var(--j-shadow-rgb) / 0.24)",
        opacity: isVisible ? 1 : 0,
        transition: `opacity ${SHEET_MS}ms var(--j-ease)`
    }));
const Panel = styled("div", {
    shouldForwardProp: (prop)=>prop !== "isVisible"
})(({ isVisible })=>({
        position: "fixed",
        zIndex: 1201,
        display: "flex",
        flexDirection: "column",
        background: "var(--j-surface)",
        color: "var(--j-ink)",
        boxShadow: "var(--j-e3)",
        outline: "none",
        "&[data-placement=bottom]": {
            left: 0,
            right: 0,
            bottom: 0,
            maxHeight: `calc(100% - ${TOP_GAP}px)`,
            borderRadius: "var(--j-r-lg) var(--j-r-lg) 0 0",
            transform: isVisible ? "none" : "translateY(100%)",
            transition: `transform ${SHEET_MS}ms var(--j-ease)`,
            [reducedMotion]: {
                transition: "none"
            }
        },
        "&[data-full=true]": {
            top: TOP_GAP
        },
        "&[data-placement=center]": {
            left: "var(--j-sp-3)",
            right: "var(--j-sp-3)",
            top: "50%",
            maxWidth: "25rem",
            maxHeight: "calc(100% - 2 * var(--j-sp-6))",
            margin: "0 auto",
            gap: "var(--j-sp-2)",
            padding: "var(--j-sp-4)",
            borderRadius: "var(--j-r-lg)",
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(-50%)" : "translateY(-50%) scale(.98)",
            transition: "opacity var(--j-dur-fade) var(--j-ease), transform var(--j-dur-fade) var(--j-ease)",
            [reducedMotion]: {
                transform: "translateY(-50%)"
            }
        },
        "& > .sheet-header": {
            height: "var(--j-hdr)",
            display: "grid",
            gridTemplateColumns: "1fr minmax(0, auto) 1fr",
            alignItems: "center",
            gap: "var(--j-sp-1)",
            padding: "0 var(--j-sp-1) 0 var(--j-sp-2)",
            borderBottom: "1px solid var(--j-line)",
            flexShrink: 0
        },
        "& .sheet-title": {
            margin: 0,
            fontSize: "var(--j-fs-lg)",
            lineHeight: "var(--j-lh-lg)",
            fontWeight: 600,
            letterSpacing: "-0.006em",
            textAlign: "center",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
        },
        "& > .sheet-dialog-title": {
            margin: 0,
            fontSize: "var(--j-fs-xl)",
            lineHeight: "var(--j-lh-xl)",
            fontWeight: 600,
            letterSpacing: "-0.006em",
            flexShrink: 0
        },
        "& .sheet-left": {
            justifySelf: "start"
        },
        "& .sheet-right": {
            justifySelf: "end"
        },
        "& > .sheet-body": {
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            padding: "var(--j-sp-2) var(--j-sp-3)",
            display: "flex",
            flexDirection: "column",
            gap: "var(--j-sp-2)"
        },
        "&[data-placement=center] > .sheet-body": {
            padding: 0
        },
        "& > .sheet-footer": {
            padding: "var(--j-sp-2) var(--j-sp-3)",
            borderTop: "1px solid var(--j-line)",
            flexShrink: 0
        },
        "& > .sheet-actions": {
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "var(--j-sp-2)",
            marginTop: "var(--j-sp-2)",
            flexShrink: 0
        }
    }));
const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), textarea, select, [tabindex]:not([tabindex="-1"])';
export function Sheet({ open, onClose, title, leftAction, rightAction, footer, fullHeight = false, placement = "bottom", "aria-label": ariaLabel, children }) {
    const { isMounted, isVisible } = usePresence(open, SHEET_MS);
    const panelRef = useRef(null);
    const titleId = useId();
    useEffect(()=>{
        if (!open) return undefined;
        const previous = document.activeElement;
        const frame = requestAnimationFrame(()=>panelRef.current?.focus());
        return ()=>{
            cancelAnimationFrame(frame);
            previous?.focus?.();
        };
    }, [
        open
    ]);
    const handleKeyDown = (event)=>{
        if (event.key === "Escape") {
            event.stopPropagation();
            onClose();
            return;
        }
        if (event.key !== "Tab" || !panelRef.current) return;
        const focusables = Array.from(panelRef.current.querySelectorAll(FOCUSABLE));
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === panelRef.current)) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    };
    if (!isMounted) return null;
    const isShown = isVisible && open;
    const isCenter = placement === "center";
    const hasHeader = !isCenter && !!(title || leftAction || rightAction);
    const hasActions = isCenter && !!(leftAction || rightAction || footer);
    return /*#__PURE__*/ createPortal(/*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Scrim, {
        isVisible: isShown,
        onClick: onClose,
        "aria-hidden": true
    }), /*#__PURE__*/ React.createElement(Panel, {
        ref: panelRef,
        isVisible: isShown,
        role: "dialog",
        "aria-modal": true,
        "aria-label": title ? undefined : ariaLabel,
        "aria-labelledby": title ? titleId : undefined,
        tabIndex: -1,
        "data-full": !isCenter && fullHeight,
        "data-placement": placement,
        onKeyDown: handleKeyDown
    }, hasHeader && /*#__PURE__*/ React.createElement("div", {
        className: "sheet-header"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "sheet-left"
    }, leftAction), title ? /*#__PURE__*/ React.createElement("h2", {
        className: "sheet-title",
        id: titleId
    }, title) : /*#__PURE__*/ React.createElement("span", null), /*#__PURE__*/ React.createElement("div", {
        className: "sheet-right"
    }, rightAction)), isCenter && title && /*#__PURE__*/ React.createElement("h2", {
        className: "sheet-dialog-title",
        id: titleId
    }, title), /*#__PURE__*/ React.createElement("div", {
        className: "sheet-body"
    }, children), !isCenter && footer && /*#__PURE__*/ React.createElement("div", {
        className: "sheet-footer"
    }, footer), hasActions && /*#__PURE__*/ React.createElement("div", {
        className: "sheet-actions"
    }, leftAction, rightAction, footer))), document.body);
}
